'use client';
import { useMemo, useState, useEffect } from 'react';
import { fuzzyFilter } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import { useGetGroups, useGetHierarchies, useGetManagementPlans } from '@practium/features/api';
import type { SystemTab, SystemPermissions } from '../types';
import { mapGroupDTO, mapHierarchyDTO, mapManagementPlanDTO } from '../utils/systemMappers';

const PAGE_SIZE = 10;

const PLANS_FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Status',
		options: [
			{ value: 'status:active', label: 'Ativo' },
			{ value: 'status:inactive', label: 'Inativo' },
		],
	},
];

export interface UseSystemPageProps {
	permissions: SystemPermissions;
	initialTab?: SystemTab;
}

export const useSystemPage = ({ permissions, initialTab = 'permissions' }: UseSystemPageProps) => {
	const [activeTab, setActiveTab] = useState<SystemTab>(initialTab);
	const [searchQuery, setSearchQuery] = useState('');
	const [sortAsc, setSortAsc] = useState(false);
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [page, setPage] = useState(1);

	const { data: groupDTOs, isLoading: isLoadingGroups } = useGetGroups({
		enabled: activeTab === 'permissions',
	});
	const { data: hierarchyDTOs, isLoading: isLoadingHierarchies } = useGetHierarchies();
	const { data: planDTOs, isLoading: isLoadingPlans } = useGetManagementPlans({
		enabled: activeTab === 'plans',
	});

	const groups = useMemo(() => {
		const countByGroupId = new Map<string, number>();
		for (const h of hierarchyDTOs) {
			for (const level of [h.farmer, h.consultant, h.company, h.master]) {
				const groupId = typeof level.group === 'string' ? level.group : level.group?._id;
				if (groupId) countByGroupId.set(groupId, (countByGroupId.get(groupId) ?? 0) + 1);
			}
		}
		return groupDTOs.map(dto => ({
			...mapGroupDTO(dto),
			hierarchyCount: countByGroupId.get(dto._id) ?? 0,
		}));
	}, [groupDTOs, hierarchyDTOs]);
	const hierarchies = useMemo(() => hierarchyDTOs.map(mapHierarchyDTO), [hierarchyDTOs]);
	const plans = useMemo(() => planDTOs.map(mapManagementPlanDTO), [planDTOs]);

	let isLoading = isLoadingHierarchies;
	if (activeTab === 'permissions') isLoading = isLoadingGroups || isLoadingHierarchies;
	else if (activeTab === 'plans') isLoading = isLoadingHierarchies || isLoadingPlans;

	useEffect(() => {
		setPage(1);
	}, [searchQuery, filterValues]);

	const handleTabChange = (tab: SystemTab) => {
		setActiveTab(tab);
		setSearchQuery('');
		setFilterValues([]);
		setPage(1);
	};

	const filterGroups = activeTab === 'plans' ? PLANS_FILTER_GROUPS : [];

	const statusFilters = filterValues.filter(v => v.startsWith('status:')).map(v => v.slice(7));

	const processedGroups = useMemo(() => {
		let result = fuzzyFilter(groups, searchQuery, g => [g.name].filter(Boolean).join(' '));
		if (sortAsc) result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		return result;
	}, [groups, searchQuery, sortAsc]);

	const processedHierarchies = useMemo(() => {
		let result = fuzzyFilter(hierarchies, searchQuery, h => [h.name, h.description].filter(Boolean).join(' '));
		if (sortAsc) result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		return result;
	}, [hierarchies, searchQuery, sortAsc]);

	const processedPlans = useMemo(() => {
		let result = fuzzyFilter(plans, searchQuery, p => [p.name, ...p.paymentMethods].filter(Boolean).join(' '));
		if (statusFilters.length > 0) result = result.filter(p => statusFilters.includes(p.status));
		if (sortAsc) result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		return result;
	}, [plans, searchQuery, filterValues, sortAsc]);

	const totalPages = useMemo(() => {
		let len = processedGroups.length;
		if (activeTab === 'hierarchies') len = processedHierarchies.length;
		else if (activeTab === 'plans') len = processedPlans.length;
		return Math.max(1, Math.ceil(len / PAGE_SIZE));
	}, [activeTab, processedGroups.length, processedHierarchies.length, processedPlans.length]);

	const filteredGroups = useMemo(
		() => processedGroups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[processedGroups, page]
	);

	const filteredHierarchies = useMemo(
		() => processedHierarchies.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[processedHierarchies, page]
	);

	const filteredPlans = useMemo(
		() => processedPlans.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[processedPlans, page]
	);

	return {
		activeTab,
		setActiveTab: handleTabChange,
		groups,
		hierarchies,
		plans,
		filteredGroups,
		filteredHierarchies,
		filteredPlans,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort: () => setSortAsc(v => !v),
		filterValues,
		setFilterValues,
		filterGroups,
		page,
		setPage,
		totalPages,
		isLoading,
		permissions,
	};
};
